import { palette, spaces } from '@kimuno/theme';
import { Box, Button, Stack, SvgIcon, type SxProps, Tooltip, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../../actions/common.actionTypes';
import { fetchMindItem as fetchMindItemAction } from '../../../../actions/mindItems.actions';
import { fetchProduct as fetchProductAction } from '../../../../actions/product.actions';
import { routes } from '../../../../constants';
import type { StoreType } from '../../../../reducers';
import { getCurrentConversation } from '../../../../reducers/aiAssistant.selectors';
import { selectMindItem } from '../../../../reducers/mindItems.selectors';
import { selectProduct } from '../../../../reducers/products.selectors';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../state/AIAssistantContextProvider';
import getMuiIcon from '../../../../utils/getMuiIcon';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import useAction from '../../../../utils/useAction';
import useRouter from '../../../../utils/useRouter';
import useScreenSize from '../../../../utils/useScreenSize';
import { formatNumberRanges } from '../utils';

const AIContextHeadline = () => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const currentConversation = useSelector(getCurrentConversation);
	const { openAssistant, displayMode } = useContext(aiAssistantContext);
	const isDialogMode = displayMode === AIAssistantDisplayMode.DIALOG;
	const mindItemContext = currentConversation?.data?.context?.mindItem;
	const { isXsScreen, isSmScreen } = useScreenSize();
	const buttonVariant = isXsScreen || isSmScreen ? 'text' : 'outlined';
	const switchUnitText = isXsScreen || isSmScreen ? 'Unit' : 'Open Unit';
	const mindItem = useSelector((state) => selectMindItem(state, mindItemContext?.id ?? ''));
	const product = useSelector((state) => selectProduct(state, mindItem?.product ?? ''));
	const mindItemStatus = useSelector(
		(state: StoreType) => state.mindItems.byId[mindItemContext?.id ?? '']?.status
	);
	const productStatus = useSelector(
		(state: StoreType) => state.products.byId[mindItem?.product ?? '']?.status
	);
	const fetchMindItem = useAction(fetchMindItemAction);
	const fetchProduct = useAction(fetchProductAction);

	const tooltipTitle = product?.title
		? `${t('Unit')}: ${product?.title} - ${mindItem?.title}`
		: `${t('Unit')}: ${mindItem?.title}`;
	// +1 because the page numbers are 1-based
	const formattedPageNumbers = formatNumberRanges(
		(mindItemContext?.pages || []).map((p: number) => p + 1)
	);
	const truncateSx: SxProps = getTextTruncationSx(1);

	// Fetch mind-item details in case we don't have them
	useEffect(() => {
		if (mindItemContext?.id && !mindItem && mindItemStatus !== AsyncActionStatus.LOADING) {
			fetchMindItem(mindItemContext.id);
		}
	}, [mindItemContext?.id, mindItem, mindItemStatus, fetchMindItem]);

	useEffect(() => {
		if (mindItem?.product && !product && productStatus !== AsyncActionStatus.LOADING) {
			fetchProduct(mindItem.product);
		}
	}, [mindItem?.product, product, productStatus, fetchProduct]);

	const handleSwitchUnit = async () => {
		router.push(`/${routes.reader.root}/${mindItemContext?.id}`);
		await openAssistant(mindItemContext?.id, currentConversation?.data?.id);
	};

	return (
		<Stack direction="row" gap={spaces.sm} alignItems="center" sx={{ width: '100%' }}>
			<Typography
				variant="subtitle2"
				sx={{
					color: palette.text.secondary,
					...truncateSx,
					minWidth: 0,
				}}
			>
				{t('Based on page {{pages}} of the unit', {
					pages: formattedPageNumbers,
				})}
			</Typography>
			<Tooltip title={tooltipTitle} arrow placement="top">
				<Box sx={{ flexShrink: 0 }}>
					<SvgIcon
						sx={{
							width: 24,
							height: 24,
						}}
						viewBox="0 0 24 24"
						component={getMuiIcon('infoCircle')}
					/>
				</Box>
			</Tooltip>
			{!isDialogMode && (
				<Button
					variant={buttonVariant}
					color="primary"
					onClick={handleSwitchUnit}
					size="small"
					sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
					data-testid="ai-assistant-switch-unit-button"
				>
					{t(switchUnitText)}
				</Button>
			)}
		</Stack>
	);
};

export default AIContextHeadline;
